import React, { useEffect, useReducer, useState } from "react"
import { Eye, EyeOff } from "lucide-react"
import { useForm } from "react-hook-form"
import Header from "../../components/navigation/Header"
import AgreementTerms from "../../components/userAuthentication/AgreementTerms"
import LoadingAnimation from "../../components/general/LoadingAnimation"
import { Link, useNavigate } from "react-router-dom"
import { faCalendar, faEnvelope, faUser } from "@fortawesome/free-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconDefinition, faChevronDown, faLock, faPhone } from "@fortawesome/free-solid-svg-icons"
import data from "../../data.json"
import axios from "axios"
import ReCAPTCHA from "react-google-recaptcha"
import Footer from "../../components/navigation/Footer"
import CreateCSFR from "../../hooks/userAuthentication/CreateCSFR"
import SignUpPartners from "../../components/userAuthentication/SignupPartners"


interface FormStructure {
  fullname: string;
  email: string;
  password: string;
  confirmpassword: string;
  birthdate: string;
  phonenumber: string;
  gender: string;
}

type Option = {
  value: string;
  selected: boolean;
  placeholder: string;
}

interface Input {
  id: string;
  name: string;
  title?: string;
  icon: string;
  type: string;
  placeholder?: string;
  required: string;
  pattern?: {
    value: string;
    message: string;
  };
  minLength?: {
    value: number;
    message: string;
  };
  maxLength?: {
    value: number;
    message: string;
  }
  options?: Option[];
}

const iconMap: Record<string, IconDefinition> = {
  faEnvelope,
  faChevronDown,
  faLock,
  faCalendar,
  faPhone,
  faUser,
}

interface Series {
  series: number;
  inputs: Input[];
  title: string;
  button: string;
}

interface State {
  currentSeries: number;
}

const initialState: State = { currentSeries: 0 }

const reducer = (state: State, action: { type: "NEXT_SERIES" }): State => {
  switch (action.type) {
    case "NEXT_SERIES":
      return { currentSeries: state.currentSeries + 1 }
    default:
      return state
  }
}

export default function SignUp() {
  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormStructure>()
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [invalidCaptcha, setInvalidCaptcha] = useState(true)
  let csrfToken = CreateCSFR({ name: "csrftoken" })
  const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false)
  const [state, dispatch] = useReducer(reducer, initialState)
  const password = watch("password") 
  const [errorEmailMessage, setErrorEmailMessage] = useState<string | undefined>(undefined)
  const [errorPasswordMessage, setErrorPasswordMessage] = useState<string | undefined>(undefined)
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const birthdate = watch("birthdate")


  function onSubmit(formData:FormStructure){
    setLoading(true)
    if (state.currentSeries < signupQuestions.length - 1) {
      dispatch({ type: "NEXT_SERIES" });
      setTimeout(() => setLoading(false), 1000);
    } else {
      axios.post(`${process.env.REACT_APP_SKILLJA_URL}/signup/`, formData, {
        headers: {
          'X-CSRFToken': csrfToken,
          'Content-Type': 'application/json',
        },
        withCredentials: true,
      })
      .then(res => {
        if (res.status === 201) {
          navigate("/auth/onboarding")
        } else {
          console.error("Signup failed")
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
    }
  }

  const handleCaptchaChange = (value: string | null) => {
    if (value) {
      setCaptchaToken(value);
      setInvalidCaptcha(false);
      axios.post(`${process.env.REACT_APP_SKILLJA_URL}/verify_captcha/`, { token: value }, {
        headers: { 'X-CSRFToken': csrfToken, 'Content-Type': 'application/json' },
        withCredentials: true,
      })
      .then(res => setInvalidCaptcha(!res.data.success))
      .catch(() => setInvalidCaptcha(true))
    } else {
      setInvalidCaptcha(true)
    }
  }

  const signupQuestions: Series[] = data.signup;
  const currentInputs = signupQuestions[state.currentSeries].inputs

  // Function to handle Google sign-in
  function handleGoogleSignUpSuccess() {
    // Simulate loading process
    const timer = setTimeout(() => {
      setLoading(false)
      navigate("/auth/onboarding")
    }, 1000)
    // End loading animation and cleanup timer
    return ()=> clearTimeout(timer)
  }

  return (
    <div className="flex flex-col h-dvh">
      <Header useCase="default" />
      <h2 className="text-2xl md:text-3xl font-semibold font-kulim mt-0 mx-auto text-center px-4">{signupQuestions[state.currentSeries].title}</h2>
      <div className="flex flex-col justify-center items-center py-12">
        {loading && state.currentSeries === signupQuestions.length - 1 ? (
          <div className="mt-20">
            <LoadingAnimation />
          </div>
        ) : (
          <>
            <form className="flex flex-col justify-center w-full mx-auto px-4 md:w-7/12 lg:w-4/12" onSubmit={handleSubmit(onSubmit)}>
              {currentInputs.map((input) => (
                <div className="relative w-full mb-3.5" key={input.id}>
                  <label htmlFor={input.id} className="sr-only">
                    {input.placeholder}
                  </label>
                  {input.type !== "select" ? (
                    <>
                      <input
                        id={input.id}
                        type={input.id !== 'password' && input.id !== 'confirmpassword' ? 
                          input.type 
                        : 
                          (input.id === 'password' ? (showPassword ? "text" : "password") : (showConfirmPassword ? "text" : "password"))
                        }
                        {...register(input.name as keyof FormStructure, {
                          required: input.required,
                          pattern: input.pattern ? { value: new RegExp(input.pattern.value), message: input.pattern.message } : undefined,
                          minLength: input.minLength ? { value: input.minLength.value, message: input.minLength.message } : undefined,
                          maxLength: input.maxLength ? { value: input.maxLength.value, message: input.maxLength.message } : undefined,
                          validate: async (val) => {
                            if (input.name === "confirmpassword" && password !== val) {
                              setErrorPasswordMessage("Passwords do not match!")
                            }
                            if (input.name === "confirmpassword" && password === val) {
                              setErrorPasswordMessage(undefined)
                            }
                            if (input.name === "email") {
                              try {
                                const response = await axios.post(`${process.env.REACT_APP_SKILLJA_URL}/does_user_exist/`, 
                                  { email: val },
                                  {
                                    headers: {
                                      "X-CSRFToken": csrfToken!,
                                      "Content-Type": "application/json",
                                    },
                                    withCredentials: true,
                                  }
                                )
                                if (response.data.exists) {
                                  setErrorEmailMessage("This email is already in use.")
                                  return false
                                } else {
                                  setErrorEmailMessage(undefined)
                                  return true
                                }
                              } catch (error) {
                                console.error("Error checking email existence:", error)
                                return "Failed to validate email. Please try again."
                              }
                            }
                            return true
                          },
                        })}
                        placeholder={input.type !== "date" ? input.placeholder : "ddd"}
                        className={`form-input ${input.type === "date" ? "date-input" : ""}`}
                        autoComplete="on"
                        onFocus={() => setShowPlaceholder(false)}
                        onBlur={() => setShowPlaceholder(true)}
                      />
                      {input.type === "date" && showPlaceholder && !birthdate && (
                        <span className="absolute left-11 top-1/2 transform -translate-y-1/2 text-main-grey-200 bg-white pointer-events-none z-10">
                          {input.placeholder}
                        </span> 
                      )}
                      {errors[input.name as keyof FormStructure]?.message && (
                        <p className="text-red-500 text-sm my-3 mx-auto text-center">
                          {errors[input.name as keyof FormStructure]?.message}
                        </p>
                      )}
                      {errorEmailMessage && input.name === "email" && (
                        <p className="text-red-500 text-sm my-3 mx-auto text-center">{errorEmailMessage}</p>
                      )}
                      {errorPasswordMessage && input.name === "confirmpassword" && (
                        <p className="text-red-500 text-sm my-3 mx-auto text-center">{errorPasswordMessage}</p>
                      )}
                      {input.type !== "date" && (
                        <FontAwesomeIcon
                          icon={iconMap[input.icon]}
                          className="absolute inset-y-4 left-0 flex items-center pl-4 text-main-grey-500"
                        />
                      )}
                      {/* Needed for IOS bugs when displaying date picker */}
                      {input.type === "date" && window.innerWidth < 747 && (
                        <FontAwesomeIcon
                          icon={iconMap[input.icon]}
                          className="absolute inset-y-4 left-0 flex items-center pl-4 text-main-grey-500"
                        />
                      )}
                      {input.id === 'password' &&
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute top-1/4 pt-0.5 right-4 flex items-center"
                        >
                          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      }
                      {input.id === 'confirmpassword' &&
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute top-1/4 pt-0.5 right-4 flex items-center"
                        >
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      }
                    </>
                  ) : (
                    <>
                      <select
                        id={input.id}
                        {...register(input.name as keyof FormStructure, { required: true })}
                        className="form-input appearance-none"
                      >
                        {input.options?.map((option, index) => (
                          <option
                            key={option.value}
                            value={index === 0 ? "" : option.value}
                            disabled={index === 0}
                          >
                            {option.placeholder}
                          </option>
                        ))}
                      </select>
                      <FontAwesomeIcon
                        icon={iconMap[input.icon]}
                        className="absolute inset-y-4 left-0 flex items-center pl-4 text-main-grey-500"
                      />
                    </>
                  )}
                </div>
              ))}
              {state.currentSeries === signupQuestions.length - 1 && <AgreementTerms isClicked={agreeToTerms} />}
              <button
                className={`w-full form-btn py-2.5 mt-3 mx-auto ${(agreeToTerms && state.currentSeries === signupQuestions.length - 1) || invalidCaptcha ? "cursor-not-allowed" : ""}`}
                type="submit"
                disabled={agreeToTerms || invalidCaptcha}
              >
                {signupQuestions[state.currentSeries].button}
              </button>
              
            </form>
            <div className="mt-5 mb-3 recaptcha-container">
              {invalidCaptcha && state.currentSeries === 0 && process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY && (
                <ReCAPTCHA
                  onChange={handleCaptchaChange}
                  sitekey={process.env.REACT_APP_RECAPTCHA_PUBLIC_KEY!}
                  onExpired={() => setCaptchaToken(null)}
                />
              )}
            </div>
            <p className="m-auto pb-1 text-main-grey-200 font-kulim">or</p>
            <div className="w-full mx-auto px-4 md:w-7/12 lg:w-4/12 mb-3">
              <SignUpPartners 
                csrfToken={csrfToken!} 
                setErrorMessage={setErrorPasswordMessage} 
                setLoading={setLoading}
                onSuccess={handleGoogleSignUpSuccess}
              />
            </div>
            <div className="bg-main-grey-300 h-0.5 w-28 lg:w-40 mt-1 mb-4" role="presentation"></div>
              <p className="text-sm text-main-grey-300 font-kulim"> 
                Already have an account? 
                <Link to={'/login'}>
                  <span className="underline cursor-pointer font-semibold text-main-green-700 ml-1 hover:text-main-green-500">
                    Login Here
                  </span>
                </Link>
              </p>
          </>
        )}
      </div>
      <div className="mb-6 mt-10">
        <Footer />
      </div>
    </div>
  )
}
